<script setup lang="ts">
import { computed, ref } from "vue";
import { UiText, UiIcon, UiNavLink } from "@shopexperts/shared";
import type { ClientInboxLeadGroup, ClientInboxProject, ProjectStatus } from "@/v2Projects/types";
import ClientProjectLine from "./ClientProjectLine.vue";
import {
  getStatusConfig,
  formatShortDate,
  getEarliestDate,
} from "@/v2Projects/utils/display";

const props = defineProps<{
  group: ClientInboxLeadGroup;
}>();

const showAdditional = ref(true);
const LEAD_URGENCY_LABELS: Record<string, string> = {
  urgent: "Urgent",
  standard: "Standard",
  as_soon_as_possible: "As soon as possible",
  within_the_next_week: "Within the next week",
  within_the_next_month: "Within the next month",
  open_to_expert_opinion: "Open to expert opinion",
};

const isSingleProject = computed(() => props.group.projects.length === 1);
const singleProject = computed(() => isSingleProject.value ? props.group.projects[0] : null);
const rootComponent = computed(() => isSingleProject.value ? UiNavLink : "article");
const rootProps = computed(() => singleProject.value
  ? {
      to: {
        name: "client-project-details",
        params: { id: String(singleProject.value.id) },
      },
    }
  : {});
const selectedExpertOrgId = computed(() => props.group.lead?.selected_expert?.org_id ?? null);

const leadStatus = computed(() => {
  if (props.group.projects.length === 0) {
    return (props.group.lead?.status ?? "pending") as ProjectStatus | string;
  }
  const statuses = props.group.projects.map((p: ClientInboxProject) => p.project_status);
  if (statuses.includes("in_progress")) return "in_progress" as ProjectStatus;
  if (statuses.includes("submitted")) return "submitted" as ProjectStatus;
  if (statuses.includes("quoted")) return "quoted" as ProjectStatus;
  if (statuses.includes("completed")) return "completed" as ProjectStatus;
  if (statuses.includes("pending")) return "pending" as ProjectStatus;
  return (statuses[0] ?? "pending") as ProjectStatus;
});

const leadStatusProjectType = computed(() => {
  if (leadStatus.value !== "pending") return null;
  const pendingProjects = props.group.projects.filter(
    (project: ClientInboxProject) => project.project_status === "pending",
  );

  if (!pendingProjects.length) return null;
  return pendingProjects.every((project: ClientInboxProject) => project.project_type === "booking_call")
    ? "booking_call"
    : "standard";
});

const statusDisplay = computed(() => {
  if (props.group.projects.length === 0) {
    if (props.group.lead?.status === "pending") {
      return {
        label: "Awaiting Match",
        badgeClass: "bg-yellow-100 text-yellow-800",
      };
    }
    return {
      label: "Matched",
      badgeClass: "bg-green-100 text-green-800",
    };
  }

  const projectStatus = leadStatus.value as ProjectStatus;
  const config = getStatusConfig(projectStatus, leadStatusProjectType.value);
  return {
    label: config.label,
    badgeClass: config.badgeClass,
  };
});

const createdDate = computed(() => {
  const dates = props.group.projects.map((p: ClientInboxProject) => p.created_at);
  if (props.group.lead?.created_at) {
    dates.unshift(props.group.lead.created_at);
  }
  return formatShortDate(getEarliestDate(dates));
});

const selectedProject = computed<ClientInboxProject | null>(() => {
  if (!selectedExpertOrgId.value) return null;
  return props.group.projects.find(
    (project: ClientInboxProject) => project.expert?.organization_id === selectedExpertOrgId.value,
  ) ?? null;
});

const additionalProjects = computed<ClientInboxProject[]>(() => {
  if (!selectedProject.value) return [];
  return props.group.projects.filter((p: ClientInboxProject) => p.id !== selectedProject.value!.id);
});

const unselectedProjects = computed<ClientInboxProject[]>(() => {
  if (selectedProject.value) return [];
  return props.group.projects;
});

const additionalCount = computed(() => additionalProjects.value.length);

function toggleAdditional() {
  showAdditional.value = !showAdditional.value;
}

const leadBudget = computed(() => props.group.lead?.budget_range ?? null);
const leadUrgency = computed(() => {
  const u = props.group.lead?.urgency;
  return u ? (LEAD_URGENCY_LABELS[u] ?? u) : null;
});
const leadBrief = computed(() => props.group.lead?.project_brief ?? null);
</script>

<template>
  <component
    :is="rootComponent"
    v-bind="rootProps"
    class="lead-card"
    :class="{ 'lead-card--clickable group/ui-button no-underline text-inherit': isSingleProject }"
  >
    <!-- Header: status + submitted date -->
    <div class="flex items-center justify-between gap-3 mb-3">
      <span
        :class="statusDisplay.badgeClass"
        class="inline-flex items-center px-2 py-0.5 rounded-sm text-[11px] font-semibold leading-4 tracking-wide"
      >
        {{ statusDisplay.label }}
      </span>
      <UiText
        v-if="createdDate"
        as="span"
        variant="p-small"
        class="text-gray-600 shrink-0"
      >
        Submitted {{ createdDate }}
      </UiText>
    </div>

    <!-- Title -->
    <UiText as="h4" variant="h5" class="font-semibold text-gray-900 leading-snug mb-2">
      {{ group.lead?.project_title ?? "Untitled Project" }}
    </UiText>

    <!-- Project lines area: bleeds to card edges -->
    <div class="projects-area">
      <!-- Single expert -->
      <template v-if="isSingleProject && singleProject">
        <ClientProjectLine :project="singleProject" :linked="false" />
      </template>

      <!-- Multiple experts: selected + additional toggle -->
      <template v-else>
        <div v-if="selectedProject">
          <ClientProjectLine :project="selectedProject" />

          <div v-if="additionalCount > 0">
            <button
              type="button"
              class="additional-toggle"
              @click.stop="toggleAdditional"
            >
              <span class="additional-toggle__line" />
              <UiText as="span" variant="p-small" class="additional-toggle__label">
                {{ additionalCount }} additional expert{{ additionalCount > 1 ? "s" : "" }}
                <UiIcon
                  name="chevron-down"
                  class="w-3 h-3 ml-0.5 inline-block transition-transform duration-200"
                  :class="{ 'rotate-180': showAdditional }"
                />
              </UiText>
              <span class="additional-toggle__line" />
            </button>

            <template v-if="showAdditional">
              <template v-for="(proj, idx) in additionalProjects" :key="proj.id">
                <div v-if="idx > 0" class="line-divider" />
                <ClientProjectLine :project="proj" compact />
              </template>
            </template>
          </div>
        </div>

        <template v-else-if="unselectedProjects.length > 0">
          <template v-for="(proj, idx) in unselectedProjects" :key="proj.id">
            <div v-if="idx > 0" class="line-divider" />
            <ClientProjectLine :project="proj" />
          </template>
        </template>

        <!-- No projects: lead details + matching state -->
        <div v-else class="matching-state">
          <div v-if="leadBudget || leadUrgency" class="flex items-center gap-2 flex-wrap mb-2">
            <UiText v-if="leadBudget" as="span" variant="p-small" class="inline-flex items-center gap-1 text-gray-700">
              <UiIcon name="dollar-circle" class="w-3.5 h-3.5 text-gray-500" />
              {{ leadBudget }}
            </UiText>
            <template v-if="leadBudget && leadUrgency">
              <span class="text-gray-400">&middot;</span>
            </template>
            <UiText v-if="leadUrgency" as="span" variant="p-small" class="text-gray-700">
              {{ leadUrgency }}
            </UiText>
          </div>
          <UiText v-if="leadBrief" as="p" variant="p-small" class="text-gray-600 truncate mb-3">
            {{ leadBrief }}
          </UiText>
          <div class="flex items-center gap-2">
            <span class="matching-dot" />
            <UiText as="span" variant="p-small" class="text-gray-700">
              Finding the right experts for your project...
            </UiText>
          </div>
        </div>
      </template>
    </div>
  </component>
</template>

<style scoped>
.lead-card {
  background: white;
  border: 1px solid var(--color-gray-200, #e5e7eb);
  border-radius: 0.75rem;
  padding: 1.25rem;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}

.lead-card:hover {
  border-color: var(--color-gray-300, #d1d5db);
  box-shadow: 0 2px 8px rgb(0 0 0 / 0.04);
}

.lead-card--clickable {
  cursor: pointer;
}

.lead-card--clickable:hover {
  border-color: var(--color-gray-400, #9ca3af);
  box-shadow: 0 4px 12px rgb(0 0 0 / 0.06);
}

.projects-area {
  margin-left: -1.25rem;
  margin-right: -1.25rem;
  border-top: 1px solid var(--color-gray-100, #f3f4f6);
}

.line-divider {
  height: 1px;
  background: var(--color-gray-100, #f3f4f6);
  margin: 0 1.25rem;
}

.additional-toggle {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.5rem 1.25rem;
  cursor: pointer;
  background: none;
  border: none;
}

.additional-toggle__line {
  flex: 1;
  height: 1px;
  background: var(--color-gray-200, #e5e7eb);
}

.additional-toggle__label {
  display: inline-flex;
  align-items: center;
  font-size: 0.6875rem;
  font-weight: 600;
  color: var(--color-gray-500, #6b7280);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  white-space: nowrap;
}

.additional-toggle:hover .additional-toggle__label {
  color: var(--color-gray-700, #374151);
}

.matching-state {
  padding: 0.75rem 1.25rem;
}

.matching-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--color-se-green, #22c55e);
  animation: pulse-dot 1.5s ease-in-out infinite;
}

@keyframes pulse-dot {
  0%, 100% { opacity: 0.4; transform: scale(0.8); }
  50% { opacity: 1; transform: scale(1.2); }
}
</style>
